import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useRegisterMachineLoss } from "./hooks/useRegisterMachineLoss";

interface Props {
  machineId: string;
  machineName: string;
  lineId: string;
}

type LossType = "REJECT" | "WASTE" | "REWORK";

const LOSS_LABELS: Record<LossType, string> = {
  REJECT: "Refugo",
  WASTE: "Desperdício",
  REWORK: "Retrabalho",
};

// Reporte de refugo por máquina (linha rastreada por máquina). Caminho único:
// o refugo da linha é a soma do reportado por máquina e a Qualidade deriva do bom consolidado.
export function ScrapEntryBlock({ machineId, machineName, lineId }: Props) {
  const register = useRegisterMachineLoss(machineId, lineId);
  const [raw, setRaw] = useState("");
  const [lossType, setLossType] = useState<LossType>("REJECT");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const empty = raw.trim() === "";

  async function handleSubmit() {
    if (empty || register.isPending) return;
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) {
      setError("Informe um inteiro maior que 0.");
      return;
    }
    setError(null);
    try {
      await register.mutateAsync({ lossType, quantity: n, reason: reason.trim() || undefined });
      toast.success(`${LOSS_LABELS[lossType]} +${n} reportado em ${machineName}.`);
      setRaw("");
      setReason("");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao reportar refugo.", { description: msg });
    }
  }

  const qtyId = `scrap-qty-${machineId}`;
  const typeId = `scrap-type-${machineId}`;
  const reasonId = `scrap-reason-${machineId}`;
  return (
    <section className="space-y-2 rounded-md border p-3">
      <header>
        <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
          <Trash2 className="h-3.5 w-3.5" />
          Reportar refugo
        </h3>
      </header>

      <form
        className="space-y-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <Label htmlFor={typeId} className="text-[11px]">
          Tipo
        </Label>
        <select
          id={typeId}
          value={lossType}
          onChange={(e) => setLossType(e.target.value as LossType)}
          className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
        >
          <option value="REJECT">Refugo</option>
          <option value="WASTE">Desperdício</option>
          <option value="REWORK">Retrabalho</option>
        </select>

        <Label htmlFor={qtyId} className="text-[11px]">
          Quantidade
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id={qtyId}
            value={raw}
            inputMode="numeric"
            placeholder="0"
            className="h-8 flex-1"
            onChange={(e) => {
              setRaw(e.target.value);
              if (error) setError(null);
            }}
          />
          <Button type="submit" size="sm" disabled={empty || register.isPending}>
            {register.isPending ? "…" : "Registrar"}
          </Button>
        </div>

        <Label htmlFor={reasonId} className="text-[11px]">
          Motivo <span className="font-normal text-muted-foreground">(opcional)</span>
        </Label>
        <Input
          id={reasonId}
          value={reason}
          maxLength={500}
          className="h-8"
          onChange={(e) => setReason(e.target.value)}
        />
        {error ? <p className="text-xs text-rd-danger-700">{error}</p> : null}
      </form>
    </section>
  );
}
